import React from 'react';
import { CaseDetail, TimelineEvent, Team, BillingSummary, BillingItem } from '../../services/api';
import { Edit, ArrowLeft, Users, DollarSign } from 'lucide-react';
import NotesTab from '../CaseDetailTabs/NotesTab';
import DocumentsTab from '../CaseDetailTabs/DocumentsTab';
import ContactsTab from '../CaseDetailTabs/ContactsTab';
import TeamsTab from '../CaseDetailTabs/TeamsTab';
import BillingTab from '../CaseDetailTabs/BillingTab';
import TimelineTab from '../CaseDetailTabs/TimelineTab';
import './CaseDetailView.css';

interface CaseDetailViewProps {
  caseDetail: CaseDetail;
  timeline: TimelineEvent[];
  teams: Team[];
  billingSummary: BillingSummary | null;
  billingItems: BillingItem[];
  billingLoading: boolean;
  activeTab: 'notes' | 'documents' | 'contacts' | 'teams' | 'billing' | 'timeline';
  onTabChange: (tab: 'notes' | 'documents' | 'contacts' | 'teams' | 'billing' | 'timeline') => void;
  onBack: () => void;
  onEdit: () => void;
  onAddNote: () => void;
  onAddContact: () => void;
  onUploadDocument: () => void;
  onDownloadDocument: (docId: string) => void;
  onPreviewDocument: (docId: string) => void;
  formatDate: (date: string) => string;
  formatDateTime: (date: string) => string;
  formatFileSize: (bytes: number) => string;
  formatCurrency: (amount: number) => string;
}

const CaseDetailView: React.FC<CaseDetailViewProps> = ({
  caseDetail,
  timeline,
  teams,
  billingSummary,
  billingItems,
  billingLoading,
  activeTab,
  onTabChange,
  onBack,
  onEdit,
  onAddNote,
  onAddContact,
  onUploadDocument,
  onDownloadDocument,
  onPreviewDocument,
  formatDate,
  formatDateTime,
  formatFileSize,
  formatCurrency,
}) => {
  return (
    <div className="case-detail-view">
      <div className="case-header">
        <div className="case-title-section">
          <div className="title-with-button">
            <h1>{caseDetail.projectName}</h1>
            <div className="case-action-buttons">
              <button onClick={onBack} className="edit-case-btn">
                <ArrowLeft size={18} />
                Back to Cases
              </button>
              <button onClick={onEdit} className="edit-case-btn">
                <Edit size={18} />
                Edit Case
              </button>
            </div>
          </div>
          <div className="case-meta">
            <span className={`status-badge status-${caseDetail.status.toLowerCase()}`}>
              {caseDetail.status}
            </span>
            <span className="phase-badge">{caseDetail.phase}</span>
          </div>
        </div>

        <div className="case-info-grid">
          <div className="info-item">
            <span className="info-label">Client:</span>
            <span className="info-value">{caseDetail.clientName}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Created:</span>
            <span className="info-value">
              {formatDate(caseDetail.createDate || caseDetail.createdAt || '')}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Last Activity:</span>
            <span className="info-value">
              {formatDate(caseDetail.lastActivity || caseDetail.lastModified || '')}
            </span>
          </div>
        </div>

        {caseDetail.description && (
          <div className="case-description">
            <span className="info-label">Description:</span>
            <span className="info-value">{caseDetail.description}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => onTabChange('notes')}
          >
            Notes ({caseDetail?.notes?.length || 0})
          </button>
          <button
            className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => onTabChange('documents')}
          >
            Documents ({caseDetail?.documents?.length || 0})
          </button>
          <button
            className={`tab ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => onTabChange('contacts')}
          >
            Contacts ({caseDetail?.contacts?.length || 0})
          </button>
          <button
            className={`tab ${activeTab === 'teams' ? 'active' : ''}`}
            onClick={() => onTabChange('teams')}
          >
            <Users size={16} style={{ marginRight: '0.25rem' }} />
            Teams ({teams.length})
          </button>
          <button
            className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
            onClick={() => onTabChange('billing')}
          >
            <DollarSign size={16} style={{ marginRight: '0.25rem' }} />
            Billing
          </button>
          <button
            className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => onTabChange('timeline')}
          >
            Timeline ({timeline.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'notes' && (
            <NotesTab
              notes={caseDetail.notes || []}
              onAddNote={onAddNote}
              formatDateTime={formatDateTime}
            />
          )}

          {activeTab === 'documents' && (
            <DocumentsTab
              documents={caseDetail.documents || []}
              onUploadDocument={onUploadDocument}
              onDownload={onDownloadDocument}
              onPreview={onPreviewDocument}
              formatFileSize={formatFileSize}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'contacts' && (
            <ContactsTab contacts={caseDetail.contacts || []} onAddContact={onAddContact} />
          )}

          {activeTab === 'teams' && <TeamsTab teams={teams} />}

          {activeTab === 'billing' && (
            <BillingTab
              billingLoading={billingLoading}
              billingSummary={billingSummary}
              billingItems={billingItems}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'timeline' && (
            <TimelineTab timeline={timeline} formatDateTime={formatDateTime} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseDetailView;

